import React, { useEffect, useRef } from "react";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right" | "none";
  duration?: number;
  once?: boolean;
  distance?: string;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className = "",
  delay = 0,
  direction = "up",
  duration = 800,
  once = true,
  distance = "50px",
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const alreadyAnimated = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Initial state (hidden)
    let translateValue = "0";
    if (direction === "up") translateValue = `translateY(${distance})`;
    else if (direction === "down") translateValue = `translateY(-${distance})`;
    else if (direction === "left") translateValue = `translateX(${distance})`;
    else if (direction === "right") translateValue = `translateX(-${distance})`;

    element.style.opacity = "0";
    element.style.transform = translateValue;
    element.style.transition = `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`;
    element.style.transitionDelay = `${delay}ms`;
    element.style.willChange = "opacity, transform";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && (!once || !alreadyAnimated.current)) {
            // When element becomes visible
            setTimeout(() => {
              element.style.opacity = "1";
              element.style.transform = "translate(0, 0)";
              alreadyAnimated.current = true;
            }, 100);

            // If animation only happens once, disconnect observer
            if (once) observer.disconnect();
          } else if (!entry.isIntersecting && !once && alreadyAnimated.current) {
            // If should animate again when out of view
            element.style.opacity = "0";
            element.style.transform = translateValue;
            alreadyAnimated.current = false;
          }
        });
      },
      {
        threshold: 0.1, // Trigger when at least 10% of element is visible
        rootMargin: "0px 0px -50px 0px", // Start animation a bit before element enters viewport
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [delay, direction, distance, duration, once]);

  return (
    <div ref={elementRef} className={className}>
      {children}
    </div>
  );
};

export default ScrollReveal;
